import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import GridBar from './grid-bar';
import GridItem from './grid-item';

import PreviewHelper from '@helper/preview';
import Store from '@app/store';
import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import Router from '@module/router';

import { itemHeight, getItemType } from '@module/transfer/preview';
import { TransferDownloadActions } from '@app/transfer-bundle/store/download-actions';

export default class Grid extends React.Component {
  static propTypes = {
    route: PropTypes.object,
    setReturnPath: PropTypes.func,
    page: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.renderItems = this.renderItems.bind(this);
  }

  componentDidUpdate() {
    if (!Browser.isMobile) {
      const downloadUrl = this.props.route.path.split('/grid')[0];
      Router.setLastRoute('transfer', downloadUrl);
    }
  }

  componentDidMount() {
    if (Router.getLastRoute('page') === '/received') {
      this.props.setReturnPath(
        '/received',
        I18n.t('page.transferReceived.title')
      );
    }
  }

  shuffleItems(items) {
    // Total height of left column, sum of all images' height
    let leftTotalHeight = 0;
    // Total height of right column, sum of all images' height
    let rightTotalHeight = 0;

    // Items rendered on the left column
    const leftItems = [];
    // Items rendered on the right column
    const rightItems = [];

    items.forEach((item) => {
      const { type, metadata } = item;
      const nodeDimensions = { height: 80, width: 400 };
      const height = itemHeight[type]
        ? itemHeight[type](nodeDimensions, metadata)
        : 80;
      if (rightTotalHeight - leftTotalHeight < 0) {
        rightItems.push(item);
        rightTotalHeight += height;
      } else {
        leftItems.push(item);
        leftTotalHeight += height;
      }
    });

    return {
      leftItems,
      rightItems,
    };
  }

  get scrollElement() {
    return (
      this.props.page &&
      this.props.page.scrollableNode &&
      this.props.page.scrollableNode.contentNode
    );
  }

  onItemClick = (item) => {
    if (Browser.isMobile) {
      return;
    }

    const {
      transfer: { id, recipient_id, secret },
    } = Store.getState();

    Router.navigateTo(
      PreviewHelper.getPreviewUrl({
        transferId: id,
        fileId: item.id,
        secret,
        recipientId: recipient_id,
      })
    );

    if (getItemType(item.file) === 'image') {
      Analytics.trackStructEvent({
        category: 'preview',
        action: 'click_item_preview',
      });
    }

    this.props.setReturnPath(
      this.props.route.path,
      I18n.t('transfer.preview.overview')
    );
  };

  onDownloadAll = async () => {
    const { transfer, session } = Store.getState();

    if (transfer.currentFileDownload) {
      return;
    }

    const { params } = this.props.route;

    await Store.dispatch(
      TransferDownloadActions.downloadTransfer({
        transfer,
        secret: params.secret,
        recipientId: params.recipient_id || false,
        domainUserId: session.domain_user_id,
      })
    );
  };

  onDownload = (item) => {
    let params = {};
    const { transfer, session } = Store.getState();

    if (this.props.route) {
      params = this.props.route.params;
    } else {
      params = Store.getState().route.params;
    }

    Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: transfer.version || params.secret,
        recipientId: params.recipient_id,
        fileId: item.id,
        domainUserId: session.domain_user_id,
      })
    );
  };

  renderItems(items) {
    const shuffledItems = this.shuffleItems(items);
    return (
      <div className="grid__layout">
        <div className="row">
          <div className="col">
            {shuffledItems.leftItems.map((item) => (
              <GridItem
                item={item}
                key={item.id}
                onDownload={this.onDownload}
                onItemClick={this.onItemClick}
                scrollElement={this.scrollElement}
              />
            ))}
          </div>
          <div className="col">
            {shuffledItems.rightItems.map((item) => (
              <GridItem
                item={item}
                key={item.id}
                onDownload={this.onDownload}
                onItemClick={this.onItemClick}
                scrollElement={this.scrollElement}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { transfer } = Store.getState();
    const items = transfer.itemsTree.items;
    /*
    Items number is taken from `transfer.items`
    even though items themselves come from `transfer.itemsTree.items`.
    The reason for that is to keep consistency with Transfer/Downloader.
    So, while displaying only top level items,
    the total number is taken from the flat list.
    */
    const itemsNumber = transfer.items.length;
    if (!items) {
      return null;
    }

    const title = I18n.t('transfer.preview.grid');
    let expires = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiry_seconds),
    });

    if (typeof transfer.expiry_seconds === 'undefined') {
      expires = I18n.t('transfer.download.expires_never');
    }

    const description = I18n.t('transfer.download.format', {
      expires,
      files: I18n.t('transfer.download.files', {
        count: itemsNumber,
        size: I18n.size(transfer.totalSize || 0, { precision: 0 }),
      }),
    });

    return (
      <div className="grid-container">
        <GridBar
          title={title}
          description={description}
          onDownloadAll={this.onDownloadAll}
        />
        {this.renderItems(items)}
      </div>
    );
  }
}
